import {
  Box,
  ButtonBase,
  IconButton,
  Stack,
  Theme,
  Typography,
  lighten,
  useTheme,
  Divider,
  Button,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  selectSections,
  selectSectionSelected,
} from "@/global/store/data/gameFilters";
import { useAppStore } from "@/global/store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { findMappedSection } from "./utils.seatMap";

interface Props {
  isOpen: boolean;
  onClickView3d?: () => void;
  onClickShowSeats?: () => void;
  onClose?: () => void;
  sectionName?: string;
}

export function SectionDetailsBottomBar(props: Props) {
  const teamConfig = useTeamConfig();
  const { images, seatMapConfigs } = teamConfig;
  const {
    isShowAvailableSeatsButtonHidden,
    isView3dButtonHidden,
    isView3dButtonHiddenBySectionName,
  } = seatMapConfigs;
  const theme = useTheme();
  const styles = useStyles(theme, {
    ...props,
    previewImage: images.seatMap3dPreviewImage,
  });
  const tooltipSection = props?.sectionName?.replace("S_", "");
  const availableSections = useAppStore(selectSections);
  const selectedSection = useAppStore(selectSectionSelected);
  const apiSectionName = availableSections?.find((itm) => {
    const section = findMappedSection(teamConfig, itm);
    return section?.mapSectionName === tooltipSection;
  });

  const isView3dButtonHiddenForSection =
    isView3dButtonHiddenBySectionName?.includes(
      apiSectionName?.displaySectionName,
    ) || false;

  return (
    <Stack direction={"column"} sx={styles.root}>
      <Box sx={styles.closeButtonWrapper}>
        <IconButton onClick={() => props?.onClose?.()}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box px={2} sx={styles.contentWrapper}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          py={2}
          ml={"2px"}
          spacing={1}
        >
          <Typography variant="body1" color="text.primary" fontWeight={600}>
            Section
          </Typography>
          <Box
            sx={{
              backgroundColor: lighten(theme.palette.primary.main, 0.9),
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            <Typography
              variant="body1"
              color={theme.palette.primary.main}
              fontWeight={600}
            >
              {apiSectionName?.displaySectionName}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {!isShowAvailableSeatsButtonHidden ? (
        <>
          <Divider />
          <Stack p={2}>
            <Button
              disabled={
                apiSectionName?.mapSectionName ===
                selectedSection?.mapSectionName
              }
              onClick={props?.onClickShowSeats}
              size="small"
              variant="contained"
              sx={{
                width: "fit-content",
                borderRadius: "6px",
              }}
            >
              Show Available Seats
            </Button>
          </Stack>
        </>
      ) : null}

      {!(isView3dButtonHidden || isView3dButtonHiddenForSection) ? (
        <>
          <Divider />
          <Box p={2}>
            <Box
              sx={styles.imageWrapper}
              onClick={(ev) => {
                ev.stopPropagation();
                props?.onClickView3d?.();
              }}
            >
              <ButtonBase sx={styles.ripple} />
              <Box sx={styles.imageTextOverlay}>
                <Box
                  className="section-details-card-image-text-wrapper"
                  sx={styles.imageTextWrapper}
                >
                  <Typography
                    variant="body1"
                    color={theme.palette.common.white}
                    fontWeight={600}
                    fontSize={"14px"}
                  >
                    Tap to View 3d
                  </Typography>
                </Box>
              </Box>
              <Box
                className="section-details-card-image"
                component={"img"}
                sx={styles.image}
                src={images.seatMap3dPreviewImage}
                title="section image"
              />
            </Box>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}

function useStyles(theme: Theme, props: Props & { previewImage: string }) {
  let { isOpen } = props;

  return {
    root: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: "16px",
      width: "100%",
      minHeight: "400px",
      overflow: "hidden",
      boxShadow: "0 0 8px 2px rgba(0,0,0,0.1)",
      position: "fixed",
      left: 0,
      bottom: 0,
      transform: isOpen ? "translateY(0)" : "translateY(100%)",
      transition: "all 0.2s ease",
      zIndex: 99,
      border: `1px solid ${theme.palette.divider}`,
    },
    closeButtonWrapper: {
      position: "absolute",
      top: "8px",
      right: "8px",
    },
    ripple: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: 2,
    },
    imageWrapper: {
      width: "100%",
      height: "250px",
      overflow: "hidden",
      position: "relative",
      borderRadius: "12px",
      cursor: "pointer",
      backgroundImage: `url(${props.previewImage})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
    },
    imageTextOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: "1",
    },
    imageTextWrapper: {
      padding: "8px 16px",
      border: `1px solid ${theme.palette.common.white}`,
      borderRadius: "24px",
      transition: "all 0.2s ease",
    },
    image: {
      width: "100%",
      height: "auto",
      objectFit: "cover",
    },
    contentWrapper: {
      // backgroundColor: theme.palette.background.paper,
    },
  };
}
